'use client';

import React, { useState, Suspense, memo, useCallback, useEffect } from 'react';
import { Header } from 'reaxl-header';
import { useSelector } from 'react-redux';
import { redirectExternal } from '../utilities';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { ErrorBoundary } from 'reaxl';
import dynamic from 'next/dynamic';
import {
  headerBadge,
  header,
} from 'axl-config';
import { mapDispatchToActionProp } from '@atc/modular-redux';
import { connect } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import {
  userDuck,
  authDuck,
  dealsDuck,
  savedInventoryDuck,
  paymentsDuck,
  recentSearchDuck,
} from '../ducks';

const AuthenticationHeader = dynamic(
  () => import('reaxl-organisms/es/AuthenticationHeader'),
  { ssr: false },
);
const headerBadgeNavItems = Object.keys(headerBadge).reduce((acc, itemKey) => ({
  ...acc,
  [itemKey]: headerBadge[itemKey],
}), {});
const AtcHeader = memo(({
  onLoggedInStateReady = () => { },
  showHeaderContent = true,
  showEmailSubscribe = false,
  showUserState = true,
  onClick = () => { },
  onSignInClick = () => { },
  onLogInSuccess = () => { },
  onSignOutClick = () => { },
  onEmailSubscribe = () => { },
  hydratePsxNotifications = () => { },
  ...rest
}) => {
  const loggedInVal = useSelector(authDuck.selectors.isLoggedIn);
  const psxNotificationsCount = useSelector(userDuck.selectors.getPsxNotificationsCount);
  const [enableAiModeLink] = useDecision('enable_atc_nav_ai_mode_link', { autoUpdate: true });
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Hydrate PSX notifications on mount
  useEffect(() => {
    if (hydratePsxNotifications) {
      hydratePsxNotifications();
    }
  }, [hydratePsxNotifications]);

  const isLoggedIn = useSelector((state) => {
    let loggedIn = typeof window !== 'undefined' ? loggedInVal : false;
    if (loggedInVal === undefined) {
      loggedIn = false;
    }
    return loggedIn;
  });
  const [littleGuyLoggedIn, setLittleGuyLoggedIn] = useState(isLoggedIn);
  const handleQuickSaveItemClick = (event, listing) => {
    redirectExternal('/cars-for-sale/vehicledetails.xhtml?listingId=' + listing.id);
  };
  const handleQuickSaveItemToggle = (event, inventory = {}, wasSaved = false) => {
    sendClick('inventorySave', event, { inventoryId: inventory.id, wasSaved });
  };
  const handleSaveInventoryUpdate = (data = {}) => {
    const {
      listing,
      alertByText,
      alertByEmail,
    } = data;
    if (alertByText) {
      sendClick('carAlertsClick', undefined, { eventResultText: 'checkbox::alert::text-car-vin', vehicle: listing });
    }
    if (alertByEmail) {
      sendClick('carAlertsClick', undefined, { eventResultText: 'checkbox::alert::email-car-vin', vehicle: listing });
    }
  };
  const handleClick = (event, data) => {
    sendClick('headerGeneralEventHandler', event, { ...data });
  };
  const onSponsorContentReady = (e, data) => {
    sendImpressions({
      name: 'headerGeneralEventHandler',
      data: { ...data, linkContext: 'PartnersImpression' },
    });
  };
  const handleOnSignOutClick = () => {
    onSignOutClick();
    setLittleGuyLoggedIn(false);
  };
  const renderHeaderContent = useCallback(() => (
    <Suspense fallback={null}>
      <ErrorBoundary>
        <AuthenticationHeader
          navItems={headerBadgeNavItems}
          userDuck={userDuck}
          authDuck={authDuck}
          dealsDuck={dealsDuck}
          recentSearchDuck={recentSearchDuck}
          savedInventoryDuck={savedInventoryDuck}
          paymentsDuck={paymentsDuck}
          showEmailSubscribe={showEmailSubscribe}
          onEmailSubscribe={onEmailSubscribe}
          onQuickSaveItemClick={handleQuickSaveItemClick}
          onQuickSaveItemToggle={handleQuickSaveItemToggle}
          onSaveInventoryUpdate={handleSaveInventoryUpdate}
          isLoggedIn={littleGuyLoggedIn}
          onLoggedInStateReady={onLoggedInStateReady}
          onClick={handleClick}
          onSignInClick={onSignInClick}
          onSignOutClick={handleOnSignOutClick}
          showUserState={showUserState}
          showPsxNotificationsCount
          psxNotificationsCount={psxNotificationsCount?.toString() || '0'}
          {...rest}
        />
      </ErrorBoundary>
    </Suspense>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [
    authDuck,
    userDuck,
    dealsDuck,
    paymentsDuck,
    headerBadgeNavItems,
    onLoggedInStateReady,
    onLogInSuccess,
    handleQuickSaveItemToggle,
    handleSaveInventoryUpdate,
    onEmailSubscribe,
    showUserState,
    psxNotificationsCount,
  ]);
  // Use default navItems during SSR to prevent hydration mismatch
  const headerNavItems = isMounted && enableAiModeLink?.enabled ? header.consolidatedNavItems : header.navItems;

  return showHeaderContent && (
    <Header
      navItems={headerNavItems}
      headerContent={renderHeaderContent()}
      onClick={handleClick}
      onSponsorContentReady={onSponsorContentReady}
      className="transparent-header gradient-background"
    />
  );
});
const mapStateToProps = (state) => ({
});
const mapDispatchToProps = mapDispatchToActionProp({
  hydrateUserInventory: userDuck.creators.hydrateUserInventory,
  hydrateRecentInventory: userDuck.creators.hydrateRecentInventory,
  hydrateRecentSearches: recentSearchDuck.creators.hydrateRecentSearches,
  hydratePsxNotifications: userDuck.creators.hydratePsxNotifications,
});
export default connect(mapStateToProps, mapDispatchToProps)(AtcHeader);
